import React, { useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import "./Dashboard.css";

import TotalUserIcon from "../../../icons/svg-cons/TotalUserIcon";
import ActiveUserIcon from "../../../icons/svg-cons/ActiveUserIcon";
import RequestPendingIcon from "../../../icons/svg-cons/RequestPendingIcon";
import InactiveUserIcon from "../../../icons/svg-cons/InactiveUserIcon";

import { WeeklyEngagement } from "../../../apis/dashboardApis";
import { useAppDispatch, useAppSelector } from "../../../redux/store/hooks";
import { fetchDashboard } from "../../../redux/slices/dashboardSlice";
import Loader from "../../../components/custom-loader/Loader";
import { getCurrentYear } from "../../../utils/getCurrentYear";
import { useNavigate } from "react-router";
import { setUserTab } from "../../../redux/slices/userNavigationSlice";
import Sidebar from "../../../layouts/sidebar/Sidebar";
import Header from "../../../layouts/header/Header";

interface Props {
  setIsAuthenticated: (auth: boolean) => void;
  isAuthenticated?: boolean;
}

const Dashboard: React.FC<Props> = ({
  setIsAuthenticated,
  isAuthenticated,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { data, loading, fetched } = useAppSelector((state) => state.dashboard);
  const currentYear = getCurrentYear();
  const [checkingSso, setCheckingSso] = useState(
    !(
      localStorage.getItem("isAuthenticated") === "true" &&
      localStorage.getItem("accessToken")
    ),
  );

  const adminDetail = JSON.parse(localStorage.getItem("adminDetail") || "{}");
  const userRole = adminDetail.role || "";
  const isStudyTeam = userRole === "study team";

  useEffect(() => {
    const fetchSSOUser = async () => {
      const existingToken = localStorage.getItem("accessToken");
      const storedAuthenticated =
        localStorage.getItem("isAuthenticated") === "true";

      if (storedAuthenticated && existingToken) {
        setCheckingSso(false);
        return;
      }

      try {
        setCheckingSso(true);

        const res = await fetch(
          `${process.env.REACT_APP_SHIBBOLETH_LOGIN_URL}/sso/user`,
          {
            method: "GET",
            credentials: "include",
          },
        );

        if (!res.ok) {
          throw new Error("SSO user request failed");
        }

        const result = await res.json();
        const ssoData = result?.data || result;
        const { details, accessToken, refreshToken } = ssoData || {};

        if (!details || !accessToken || !refreshToken) {
          throw new Error("SSO user response missing auth data");
        }

        localStorage.setItem("isAuthenticated", "true");
        localStorage.setItem("accessToken", accessToken);
        localStorage.setItem("refreshToken", refreshToken);
        localStorage.setItem(
          "adminDetail",
          JSON.stringify({
            id: details.id,
            email: details.email,
            role: details.role.name,
            name: details.name,
          }),
        );

        setIsAuthenticated(true);
      } catch (err) {
        console.error("SSO user fetch failed:", err);
        localStorage.removeItem("isAuthenticated");
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("adminDetail");
        setIsAuthenticated(false);
        navigate("/login", { replace: true });
      } finally {
        setCheckingSso(false);
      }
    };

    fetchSSOUser();
  }, [navigate, setIsAuthenticated]);

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    const storedAuthenticated =
      localStorage.getItem("isAuthenticated") === "true";

    if (checkingSso || !storedAuthenticated || !token) return;

    if (!fetched) {
      dispatch(
        fetchDashboard({
          startDate: `${currentYear}-01-01`,
          endDate: `${currentYear}-12-31`,
        }),
      );
    }
  }, [dispatch, fetched, currentYear, isAuthenticated, checkingSso]);

  const getMetricValue = (name: string): number =>
    (data.find((m) => m.name === name)?.count as number) || 0;

  const weeklyEngagement: WeeklyEngagement[] =
    (data.find((m) => m.name === "Users Engaged Per Week")
      ?.count as WeeklyEngagement[]) || [];

  const stats = useMemo(() => {
    const baseStats = [
      {
        label: "Total Approved Users",
        value: getMetricValue("Total Users"),
        icon: <TotalUserIcon />,
      },
      {
        label: "Active Users",
        value: getMetricValue("Active Users"),
        icon: <ActiveUserIcon />,
      },
      {
        label: "Inactive Users",
        value: getMetricValue("Inactive Users"),
        icon: <InactiveUserIcon />,
      },
    ];

    if (!isStudyTeam) {
      baseStats.push({
        label: "Request Pending",
        value: getMetricValue("Pending Users"),
        icon: <RequestPendingIcon />,
      });
    }

    return baseStats;
  }, [data, isStudyTeam]);

  const handleStatClick = (label: string) => {
    if (
      label === "Total Users" ||
      label === "Active Users" ||
      label === "Total Approved Users"
    ) {
      dispatch(setUserTab("Active"));
      navigate("/users");
    } else if (label === "Inactive Users") {
      dispatch(setUserTab("Inactive"));
      navigate("/users");
    } else if (label === "Request Pending") {
      navigate("/user-request");
    }
  };

  const chartOptions: ApexOptions = {
    chart: { id: "user-engagement", toolbar: { show: false } },
    xaxis: {
      categories: weeklyEngagement.map((w) => w.name.replace("Week ", "")),
      title: { text: "Weeks" },
    },
    yaxis: { title: { text: "Users" } },
    dataLabels: { enabled: true, offsetY: -10 },
    colors: ["#5CE1E6"],
    plotOptions: {
      bar: {
        borderRadius: 15,
        borderRadiusApplication: "start" as any,
        horizontal: false,
      },
    },
    states: {
      hover: { filter: { type: "none" } },
      active: { filter: { type: "none" } },
    },
  };

  const chartSeries = [
    {
      name: "Users Engaged",
      data: weeklyEngagement.map((w) => Number(w.users || 0)),
    },
  ];

  if (checkingSso) {
    return <Loader />;
  }

  if (
    localStorage.getItem("isAuthenticated") !== "true" ||
    !localStorage.getItem("accessToken")
  ) {
    return null;
  }

  return (
    <div>
      <Header setIsAuthenticated={setIsAuthenticated} />
      <div style={{ display: "flex" }}>
        <Sidebar isAuthenticated={isAuthenticated} />
        <div className="dashboard-container">
          <h1 className="dashboard-header">Dashboard</h1>

          {loading ? (
            <Loader />
          ) : (
            <>
              <div className="stats-cards">
                {stats.map((stat, idx) => (
                  <div
                    key={idx}
                    className="stat-card"
                    onClick={() => handleStatClick(stat.label)}
                  >
                    <div>
                      <div className="stat-label">{stat.label}</div>
                      <div className="stat-value">{stat.value}</div>
                    </div>
                    <div className="stat-icon">{stat.icon}</div>
                  </div>
                ))}
              </div>

              <Chart
                options={chartOptions}
                series={chartSeries}
                type="bar"
                height={300}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
